import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RichText from "@/components/RichText";
import { publicUrl } from "@/lib/storage";

type Cause = {
  id: string;
  name: string;
  blurb: string | null;
  raised: number;
  goal: number;
  donate_url: string | null;
  logo_path: string | null;
  image_paths: string[] | null;
  published: boolean;
};

type AthleteRef = { id: string; slug: string; name: string; image_path: string | null };

const CauseDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [cause, setCause] = useState<Cause | null>(null);
  const [athletes, setAthletes] = useState<AthleteRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      const { data: c } = await supabase
        .from("causes")
        .select("*")
        .eq("id", id)
        .eq("published", true)
        .maybeSingle();
      if (!c) { setCause(null); setLoading(false); return; }
      setCause(c as any);

      const { data: links } = await supabase
        .from("cause_athletes")
        .select("athlete_id")
        .eq("cause_id", id);
      const aIds = (links ?? []).map((r: any) => r.athlete_id);
      if (aIds.length) {
        const { data: aData } = await supabase
          .from("athletes")
          .select("id,slug,name,image_path")
          .in("id", aIds)
          .eq("published", true);
        setAthletes((aData ?? []) as any);
      }
      setLoading(false);
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-24 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!cause) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-24 text-center">
          <h1 className="font-display text-4xl mb-4">Cause not found</h1>
          <Link to="/causes" className="text-primary underline">Back to causes</Link>
        </div>
      </div>
    );
  }

  const blurbIsHtml = !!cause.blurb && /<\/?[a-z][\s\S]*>/i.test(cause.blurb);
  const logoUrl = publicUrl(cause.logo_path);
  const gallery = (cause.image_paths ?? []).map((p) => publicUrl(p)).filter(Boolean) as string[];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="bg-gradient-warm py-16 md:py-24">
          <div className="container max-w-3xl">
            <Link to="/causes" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary">← Back to causes</Link>
            <div className="flex items-center gap-5 mt-4">
              {logoUrl && (
                <div className="w-20 h-20 rounded-2xl bg-card shadow-card overflow-hidden flex items-center justify-center shrink-0">
                  <img src={logoUrl} alt={`${cause.name} logo`} className="w-full h-full object-contain p-2" />
                </div>
              )}
              <div>
                <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-primary">Give back</span>
                <h1 className="font-display text-5xl md:text-7xl mt-2 leading-none text-balance">{cause.name}</h1>
              </div>
            </div>

            {cause.donate_url && (
              <a
                href={cause.donate_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-block px-8 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors"
              >
                Contribute ↗
              </a>
            )}
          </div>
        </section>

        {cause.blurb && (
          <section className="container py-16 max-w-3xl">
            <h2 className="font-display text-3xl mb-4">About the cause</h2>
            <div className="text-muted-foreground text-lg leading-relaxed">
              {blurbIsHtml ? <RichText html={cause.blurb} /> : <p>{cause.blurb}</p>}
            </div>
          </section>
        )}

        {gallery.length > 0 && (
          <section className="container pb-16 max-w-3xl">
            <h2 className="font-display text-3xl mb-6">Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gallery.map((url, i) => (
                <button
                  key={`${url}-${i}`}
                  onClick={() => setLightbox(url)}
                  className="aspect-square rounded-2xl overflow-hidden bg-muted shadow-card hover:shadow-bold transition-shadow"
                >
                  <img src={url} alt={`${cause.name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </section>
        )}

        {athletes.length > 0 && (
          <section className="container pb-16 max-w-3xl">
            <h2 className="font-display text-3xl mb-6">Backed by</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {athletes.map((a) => (
                <Link key={a.id} to={`/athlete/${a.slug}`} className="flex items-center gap-4 p-4 rounded-2xl bg-card shadow-card hover:shadow-bold transition-shadow">
                  <div className="w-16 h-16 rounded-full bg-muted overflow-hidden shrink-0">
                    {a.image_path && <img src={publicUrl(a.image_path)!} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div>
                    <p className="font-semibold">{a.name}</p>
                    <p className="text-xs text-primary font-semibold uppercase tracking-wider mt-1">View profile →</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-background/95 backdrop-blur flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-2xl shadow-bold" />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-card text-foreground font-bold"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default CauseDetail;
